import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  Typography,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import UsersFilterDialog from "../../components/UserManegement/UsersFilterDialog";
import UserDialog from "../../components/UserManegement/UserDialog";
import UserConfirmDialog from "../../components/UserManegement/UserConfirmDialog";
import UserResendDialog from "../../components/UserManegement/UserResendDialog";
import { searchStyles } from "../../styles/formStyles";
import { IUserListDTO } from "../../interfaces/UserManagement";
import { getUserList, searchFilterUsers } from "../../services/UserManagement";

const Users =() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<Array<IUserListDTO>>([]);
  const [loading, setLoading] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [filters, setFilters] = useState<{ roles: number[]; departments: number[]; statuses: string[] }>({ roles: [], departments: [], statuses: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUserListDTO | null>(null);
  const [selectedResendUser, setSelectedResendUser] = useState<IUserListDTO | null>(null);
  const [editingUser, setEditingUser] = useState<IUserListDTO | null>(null);
  const rowsPerPage = 7;

  useEffect(() => {
    if (searchParams.get("action") === "invite") {
      handleAdd();
      setSearchParams({});
    }
  }, [searchParams]);

  const fetchUserList = async () => {
    try {
      setLoading(true);
      const res = await getUserList();
      setUsers(res && Array.isArray(res) ? res : []);
    } catch (error) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchFilterUsers(filters.statuses, filters.roles, filters.departments, value);
        setUsers(Array.isArray(res) ? res : []);
        setPage(1);
      } catch {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    fetchUserList();
  }, []);
  const paginatedData = users.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(users.length / rowsPerPage);

  const handleAdd = () => {
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleEdit = (user: IUserListDTO) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleSave = () => {
    setDialogOpen(false);
    fetchUserList();
  };

  const handleConfirmStatusSaved = () => {
    setSelectedUser(null);
    fetchUserList();
  };

  const handleResendSaved = () => {
    setSelectedResendUser(null);
  };

  return (
    <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", p:1, gap: 2 }}>
      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ p:1, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, borderBottom: "1px solid", borderColor: "grey.100" }}>
          <TextField
            placeholder="Search by User name, ID"
            size="small"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "grey.500" }} />
                </InputAdornment>
              ),
            }}
            sx={searchStyles}
          />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              startIcon={<FilterAltOutlinedIcon />}
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={{ borderRadius: "6px", border: "1px solid rgba(74, 85, 101, 0.10)", backgroundColor: "#FFF", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", textTransform: "none", color: "text.primary", px: 2, "&:hover": { backgroundColor: "#FFF" } }}
            >
              Filter
            </Button>
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ borderRadius: "6px", textTransform: "none", px: 2, "&:hover": { backgroundColor: "primary.main" } }}
            >
              Invite User
            </Button>
          </Box>
        </Box>

        <Box sx={{ p:1, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Paper sx={{ maxHeight: "500px", overflow: "auto", "&::-webkit-scrollbar": { width: "8px", height: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>User Id</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>User name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Department</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Role</TableCell>

                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", p: 1 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No users available</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => (
                    <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.id}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.firstName} {row.lastName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.departmentName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.roleName}</TableCell>

                      <TableCell align="center" sx={{ p: 1 }}>
                        <Typography
                          sx={{
                            bgcolor: row.status === "Active" ? "success.light" : "error.light",
                            color: row.status === "Active" ? "success.main" : "error.main",
                            textAlign: "center",
                            py: 0.5,
                            px: 1,
                            borderRadius: "6px",
                            display: "inline-block",
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            minWidth: "80px",
                          }}
                        >
                          {row.status}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
                          <Button
                            startIcon={<Box component="img" src="/edit.svg" alt="Edit" sx={{ width: "1.2rem", height: "1.2rem" }} />}
                            onClick={() => handleEdit(row)}
                            sx={{ color: "primary.main", textTransform: "none", fontSize: "0.8rem", minWidth: "70px", "&:hover": { backgroundColor: "transparent" } }}
                          >
                            Edit
                          </Button>
                          {row.status === "Inactive" &&
                            <Button
                              startIcon={<SendIcon />}
                              onClick={() => setSelectedResendUser(row)}
                              sx={{ color: "grey.700", textTransform: "none", fontSize: "0.8rem", minWidth: "90px", "&:hover": { backgroundColor: "transparent" } }}
                            >
                              Resend
                            </Button>
                          }
                          
                          {row.status === "Active" ? (
                            <Button
                              startIcon={<CloseIcon />}
                              onClick={() => setSelectedUser(row)}
                              sx={{ color: "error.main", textTransform: "none", fontSize: "0.8rem", minWidth: "110px", "&:hover": { backgroundColor: "transparent" } }}
                            >
                              Inactivate
                            </Button>
                          ) : (
                            <Button
                              startIcon={<CheckIcon />}
                              onClick={() => setSelectedUser(row)}
                              sx={{ color: "success.main", textTransform: "none", fontSize: "0.8rem", minWidth: "110px", "&:hover": { backgroundColor: "transparent" } }}
                            >
                              Activate
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Paper>

      {users.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" />
        </Box>
      )}

      <UsersFilterDialog
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        search={search}
        onApply={(newFilters, data) => {
          setFilters(newFilters);
          setUsers(data);
          setPage(1);
          setFilterAnchor(null);
        }}
      />

      <UserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        user={editingUser}
      />

      <UserConfirmDialog
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        onSave={handleConfirmStatusSaved}
        user={selectedUser}
      />

      <UserResendDialog
        open={!!selectedResendUser}
        onClose={() => setSelectedResendUser(null)}
        onSave={handleResendSaved}
        user={selectedResendUser}
      />
    </Box>
  );
};
export default Users;
